import React, { forwardRef, useMemo } from "react";
import type { CSSProperties } from "react";
import type { ThemeTokens } from "./types";
import { tplThemeVars } from "./templateThemeVars";
import { mergeTemplateRefs, useTemplateStyles } from "./useTemplateStyles";

export interface TemplateThemeRootProps
  extends React.HTMLAttributes<HTMLElement> {
  T: ThemeTokens;
  as?: keyof React.JSX.IntrinsicElements;
  /** Merged after theme token variables (still applied via ref, not inline `style`). */
  cssVars?: CSSProperties;
}

/**
 * Theme host: applies `tplThemeVars(T)` on the element via ref so children can use `var(--tpl-*)`.
 */
export const TemplateThemeRoot = forwardRef<HTMLElement, TemplateThemeRootProps>(
  function TemplateThemeRoot(
    { T, as: Tag = "div", className, cssVars, children, ...rest },
    forwardedRef,
  ) {
    const merged = useMemo(
      () => ({ ...tplThemeVars(T), ...cssVars }) as CSSProperties,
      [T, cssVars],
    );
    const themeRef = useTemplateStyles<HTMLElement>(merged);
    return React.createElement(
      Tag,
      {
        ...rest,
        ref: mergeTemplateRefs(themeRef, forwardedRef),
        className,
      },
      children,
    );
  },
);
